import {
  Badge,
  Button,
  Checkbox,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sea-snag/ui';
import { Check, ChevronsUpDown, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { LocationHierarchyCountry } from '../lib/api-client';

// ── Single-select dropdown (used for Country) ──

interface SingleSelectDropdownProps {
  label: string;
  placeholder: string;
  items: Array<{ pid: string; label: string }>;
  selectedPid?: string;
  onChange: (pid: string | undefined) => void;
  disabled: boolean;
}

function SingleSelectDropdown({
  label,
  placeholder,
  items,
  selectedPid,
  onChange,
  disabled,
}: SingleSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const selectedLabel = useMemo(
    () => items.find((it) => it.pid === selectedPid)?.label,
    [items, selectedPid]
  );

  function handleSelect(pid: string) {
    onChange(pid === selectedPid ? undefined : pid);
    setOpen(false);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
            onClick={() => !disabled && setOpen((prev) => !prev)}
            type="button"
          >
            <span className="truncate">
              {selectedLabel ? (
                selectedLabel
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {selectedPid !== undefined && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange(undefined);
                      setOpen(false);
                    }}
                  >
                    <span className="text-muted-foreground italic">Clear selection</span>
                  </CommandItem>
                )}
                {items.map((item) => {
                  const isSelected = item.pid === selectedPid;
                  return (
                    <CommandItem
                      key={item.pid}
                      value={item.label}
                      onSelect={() => handleSelect(item.pid)}
                    >
                      <Check
                        className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                      <span className="truncate">{item.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Multi-select dropdown (used for Sailing Area + Base) ──

interface MultiSelectDropdownProps {
  label: string;
  placeholder: string;
  items: Array<{ pid: string; label: string }>;
  selectedPids: string[];
  onChange: (pids: string[]) => void;
  disabled: boolean;
}

function MultiSelectDropdown({
  label,
  placeholder,
  items,
  selectedPids,
  onChange,
  disabled,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedPids), [selectedPids]);
  const selectedItems = useMemo(
    () => items.filter((it) => selectedSet.has(it.pid)),
    [items, selectedSet]
  );

  function toggle(pid: string): void {
    if (selectedSet.has(pid)) {
      onChange(selectedPids.filter((p) => p !== pid));
    } else {
      onChange([...selectedPids, pid]);
    }
  }

  function removeChip(pid: string): void {
    onChange(selectedPids.filter((p) => p !== pid));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
            onClick={() => !disabled && setOpen((prev) => !prev)}
            type="button"
          >
            <span className="truncate">
              {selectedPids.length > 0 ? (
                <Badge variant="secondary" className="text-xs">
                  {selectedPids.length} selected
                </Badge>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {items.map((item) => {
                  const isSelected = selectedSet.has(item.pid);
                  return (
                    <CommandItem
                      key={item.pid}
                      value={item.label}
                      onSelect={() => toggle(item.pid)}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="mr-2 pointer-events-none"
                        tabIndex={-1}
                      />
                      <span className="truncate">{item.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedItems.map((it) => (
            <Badge key={it.pid} variant="secondary" className="gap-1 pr-1 text-xs">
              <span className="max-w-[140px] truncate">{it.label}</span>
              <button
                type="button"
                onClick={() => removeChip(it.pid)}
                className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
                aria-label={`Remove ${it.label}`}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── LocationCascadeSingle ──
//
// Country → Sailing Area → Base. Country is single-select and drives the
// cascade (mirroring the MMK agency portal). Sailing Area + Base are
// multi-select within that country. Child levels are disabled until the
// parent level has a selection.
//
// Selecting/clearing a parent invalidates child selections so the URL
// state never holds (sailingAreaPids ∉ chosen country) or (basePids ∉
// chosen sailing areas).

interface LocationCascadeSingleProps {
  countries: LocationHierarchyCountry[];
  countryPid?: string;
  sailingAreaPids: string[];
  basePids: string[];
  onCountryChange: (pid: string | undefined) => void;
  onSailingAreaPidsChange: (pids: string[]) => void;
  onBasePidsChange: (pids: string[]) => void;
}

export function LocationCascadeSingle({
  countries,
  countryPid,
  sailingAreaPids,
  basePids,
  onCountryChange,
  onSailingAreaPidsChange,
  onBasePidsChange,
}: LocationCascadeSingleProps) {
  const countryItems = useMemo(
    () => countries.map((c) => ({ pid: c.pid, label: c.name })),
    [countries]
  );

  const selectedCountry = useMemo(
    () => countries.find((c) => c.pid === countryPid),
    [countries, countryPid]
  );

  const sailingAreaItems = useMemo(
    () => (selectedCountry?.sailingAreas ?? []).map((sa) => ({ pid: sa.pid, label: sa.name })),
    [selectedCountry]
  );

  // Bases visible = union of bases across all selected sailing areas. If
  // nothing selected yet, base dropdown stays disabled (see prop below).
  const baseItems = useMemo(() => {
    if (!selectedCountry || sailingAreaPids.length === 0) return [];
    const selectedAreaSet = new Set(sailingAreaPids);
    const seen = new Set<string>();
    const items: Array<{ pid: string; label: string }> = [];
    for (const sa of selectedCountry.sailingAreas) {
      if (!selectedAreaSet.has(sa.pid)) continue;
      for (const b of sa.bases) {
        if (seen.has(b.pid)) continue;
        seen.add(b.pid);
        items.push({ pid: b.pid, label: b.name });
      }
    }
    return items;
  }, [selectedCountry, sailingAreaPids]);

  function handleCountryChange(pid: string | undefined): void {
    onCountryChange(pid);
    // Cascade: changing/clearing country invalidates all children.
    onSailingAreaPidsChange([]);
    onBasePidsChange([]);
  }

  function handleSailingAreaPidsChange(pids: string[]): void {
    onSailingAreaPidsChange(pids);
    // Cascade: prune any selected base that's no longer reachable from
    // the new sailing-area selection.
    if (basePids.length > 0 && selectedCountry) {
      const newAreaSet = new Set(pids);
      const stillReachable = new Set<string>();
      for (const sa of selectedCountry.sailingAreas) {
        if (!newAreaSet.has(sa.pid)) continue;
        for (const b of sa.bases) stillReachable.add(b.pid);
      }
      const pruned = basePids.filter((p) => stillReachable.has(p));
      if (pruned.length !== basePids.length) onBasePidsChange(pruned);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <SingleSelectDropdown
        label="Country"
        placeholder="Select country..."
        items={countryItems}
        selectedPid={countryPid}
        onChange={handleCountryChange}
        disabled={false}
      />
      <MultiSelectDropdown
        label="Sailing Areas"
        placeholder={countryPid ? 'Select sailing areas...' : 'Pick a country first'}
        items={sailingAreaItems}
        selectedPids={sailingAreaPids}
        onChange={handleSailingAreaPidsChange}
        disabled={!countryPid}
      />
      <MultiSelectDropdown
        label="Bases"
        placeholder={
          sailingAreaPids.length > 0
            ? 'Select bases...'
            : countryPid
              ? 'Pick a sailing area first'
              : 'Pick a country first'
        }
        items={baseItems}
        selectedPids={basePids}
        onChange={onBasePidsChange}
        disabled={sailingAreaPids.length === 0}
      />
    </div>
  );
}
